import { defineComponent as _defineComponent } from 'vue'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue';
import { PlayerIcon } from '@/enums/player-icon';

import VideoPlayerWrapper from '@/ui-components/VideoPlayerWrapper.vue';
import { useMounted } from '@vueuse/core';
import { TrailerClip } from '@/interfaces/trailerClip';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { Clip, ClipProvider } from '@/@types/graphql-types';

import Spinner from '@/ui-components/Spinner.vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { storeToRefs } from 'pinia';
import { SnowplowContext } from '@/helpers/tracking/providers';

interface DailyMotionPlayerProps {
	clip: TrailerClip | Clip;
	title?: TitleDetail;
	isActive?: boolean;
	isBackdrop?: boolean;
	playIconType?: PlayerIcon | null;
	isDisneyPlusOffer?: boolean;
	isTitleTrailer?: boolean;
	// JW_APP_DOWNLOAD_EXP_1
	isJwAppDlExp?: boolean;
	// JW_APP_DOWNLOAD_EXP_1

	additionalContexts?: SnowplowContext[];
	hideTrailerAd?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DailyMotionPlayer',
  props: {
    clip: null,
    title: null,
    isActive: { type: Boolean, default: false },
    isBackdrop: { type: Boolean, default: false },
    playIconType: { default: null },
    isDisneyPlusOffer: { type: Boolean, default: false },
    isTitleTrailer: { type: Boolean },
    isJwAppDlExp: { type: Boolean, default: false },
    additionalContexts: null,
    hideTrailerAd: { type: Boolean, default: false }
  },
  emits: ["setActiveClipId", "onPlaying"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'setActiveClipId', clipId: string): void;
	(e: 'onPlaying', clip: DailyMotionPlayerProps['clip']): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { clip: TrailerClip | Clip, title?: TitleDetail, isActive: boolean, isBackdrop: boolean, playIconType: PlayerIcon | null, isDisneyPlusOffer: boolean, isTitleTrailer?: boolean, isJwAppDlExp: boolean, additionalContexts?: SnowplowContext[], hideTrailerAd: boolean };

const BitmovinPlayer = defineAsyncComponent({
	loader: () => import(/* webpackChunkName: "bitmovin-player" */ './BitmovinPlayer.vue'),
	loadingComponent: Spinner,
});
const JustWatchTVPlayBtn = () => import('@/pages/titleDetailV1/v1/hero/JustwatchTVPlayBtn.vue');

const isMounted = useMounted();
const { isPremium } = storeToRefs(useUserStore());





const playerIdsByType = {
	DEFAULT_TRAILERS: 'xb9cu',
	NO_ADS_TRAILERS: 'x1dwv8',

	JUSTWATCH_TV_DEFAULT: 'x10mxm',
	JUSTWATCH_TV_NO_ADS: 'x1b8c8',
	JUSTWATCH_TV_NO_AUTO_PLAY: 'x1bfya',
};

const isJustWatchTV = computed(() => props.clip.provider === 'JUSTWATCH_TV');
const isBitMovinVideo = computed(() => {
	return (
		props.clip.provider === 'OTT' ||
		props.clip.provider === ClipProvider.Justwatchtv ||
		props.clip.sourceUrl?.includes('dailymotion.com/cdn/manifest') ||
		props.clip.externalId === 'b2Z8dG04Nzg3MjpERTpqd3Q6ZnJlZTpoZA=='
	);
});
const playerId = computed(() => {
	if (isJustWatchTV.value) {
		if (isPremium.value) {
			return playerIdsByType['JUSTWATCH_TV_NO_ADS'];
		}
		return playerIdsByType['JUSTWATCH_TV_DEFAULT'];
	}
	if (props.hideTrailerAd) {
		return playerIdsByType['NO_ADS_TRAILERS'];
	}
	return playerIdsByType['DEFAULT_TRAILERS'];
});
const srcUrl = computed(() => {
	// JW_APP_DOWNLOAD_EXP_1
	if (props.isJwAppDlExp && props.clip.sourceUrl) {
		// prettier-ignore
		return `${props.clip.sourceUrl.replace(playerIdsByType['JUSTWATCH_TV_DEFAULT'], playerIdsByType['JUSTWATCH_TV_NO_AUTO_PLAY'])}&autoplay=0&controls=1`;
	}
	// JW_APP_DOWNLOAD_EXP_1

	if (isPremium.value && isJustWatchTV.value && props.clip.sourceUrl) {
		const url = new URL(props.clip.sourceUrl);
		const externalId = url.searchParams.get('video');
		if (externalId) {
			// prettier-ignore
			return `${props.clip.sourceUrl.replace(playerIdsByType['JUSTWATCH_TV_DEFAULT'], playerIdsByType['JUSTWATCH_TV_NO_ADS'])}`;
		}
	}

	if (props.clip.sourceUrl && !props.hideTrailerAd) return props.clip.sourceUrl;

	return `https://geo.dailymotion.com/player/${playerId.value}.html?video=${props.clip.externalId}`;
});

function play() {
	emit('setActiveClipId', props.clip.externalId);

	// onPlaying triggers scrolling to active clip. setTimeout prevents onPlaying triggering scroll events before activeClip is updated
	setTimeout(() => {
		emit('onPlaying', props.clip);
	}, 200);
}

return { __sfc: true,BitmovinPlayer, JustWatchTVPlayBtn, isMounted, isPremium, props, emit, playerIdsByType, isJustWatchTV, isBitMovinVideo, playerId, srcUrl, play, VideoPlayerWrapper }
}

})